import { z } from "zod";
import type { ExpertAiContext, UpdateExpertAiContextRequest } from "@shopexperts/legacy-api";

/** Mirrors `UpdateExpertAiContextRequest` on the API: each field `nullable|string|max:20000`. */
export const AI_CONTEXT_MAX_LENGTH = 20_000;

const contextField = z.string().max(AI_CONTEXT_MAX_LENGTH, `Keep this under ${AI_CONTEXT_MAX_LENGTH.toLocaleString("en-US")} characters.`);

export const aiContextFormSchema = z.object({
  additional_public_data: contextField,
  admin_context: contextField,
  must_include: contextField,
  must_avoid: contextField,
});

export type AiContextFormValues = z.infer<typeof aiContextFormSchema>;

export type AiContextTrust = "public" | "verified" | "authoritative";

export interface AiContextFieldDefinition {
  name: keyof AiContextFormValues;
  label: string;
  trust: AiContextTrust;
  hint: string;
  placeholder: string;
}

export const AI_CONTEXT_FIELDS: readonly AiContextFieldDefinition[] = [
  {
    name: "additional_public_data",
    label: "Additional public data",
    trust: "public",
    hint: "Self-reported facts the profile is missing. The model may paraphrase these but never states them as verified.",
    placeholder: "Runs a Shopify Plus agency in Berlin with 12 developers…",
  },
  {
    name: "admin_context",
    label: "Admin context",
    trust: "verified",
    hint: "Things you know to be true about this expert. Stated as fact in the summaries.",
    placeholder: "Delivered three enterprise migrations we audited in 2025…",
  },
  {
    name: "must_include",
    label: "Must include",
    trust: "authoritative",
    hint: "Statements every summary for this expert has to contain.",
    placeholder: "Mention the 10-year track record with Shopify Plus.",
  },
  {
    name: "must_avoid",
    label: "Must avoid",
    trust: "authoritative",
    hint: "Statements, topics, or phrasing the summaries must never contain.",
    placeholder: "Do not mention pricing or the previous agency name.",
  },
];

export const AI_CONTEXT_TRUST_LABELS: Record<AiContextTrust, string> = {
  public: "Public",
  verified: "Verified",
  authoritative: "Authoritative",
};

export const AI_CONTEXT_TRUST_DESCRIPTIONS: Record<AiContextTrust, string> = {
  public: "Self-reported. May be paraphrased, never restated as a verified fact.",
  verified: "May be stated as fact.",
  authoritative: "An instruction that overrides everything the expert data says.",
};

export function aiContextToFormValues(context: ExpertAiContext | null | undefined): AiContextFormValues {
  return {
    additional_public_data: context?.additional_public_data ?? "",
    admin_context: context?.admin_context ?? "",
    must_include: context?.must_include ?? "",
    must_avoid: context?.must_avoid ?? "",
  };
}

/** Only changed fields travel; the API treats a blank string as "clear". */
export function aiContextChanges(
  before: AiContextFormValues,
  after: AiContextFormValues,
): UpdateExpertAiContextRequest {
  const body: UpdateExpertAiContextRequest = {};
  for (const field of AI_CONTEXT_FIELDS) {
    if (before[field.name] !== after[field.name]) body[field.name] = after[field.name];
  }
  return body;
}

export function hasAiContextValues(context: ExpertAiContext | null | undefined): boolean {
  if (!context) return false;
  return AI_CONTEXT_FIELDS.some((field) => Boolean(context[field.name]?.trim()));
}
